import { Badge } from '@monksflow/monks-ui';
import { TriangleAlertIcon, XIcon } from 'lucide-react';
import type { ConnectorPlatform } from '@/entities/media-connector';
import { CONNECTOR_PLATFORM_ICONS, CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import {
  countGroup,
  countRows,
  leafLevelOf,
  levelPluralLabel,
  type JobPlatformGroup,
} from '@/entities/media-campaign';
import { useTranslation } from 'react-i18next';

interface JobPlatformTabsProps {
  groups: JobPlatformGroup[];
  value: ConnectorPlatform;
  onChange: (next: ConnectorPlatform) => void;
  /** False until pre-flight has run — error counts stay hidden until then. */
  showIssues: boolean;
  /** Absent while a job may no longer change, so the control is not offered then. */
  onRemove?: (platform: ConnectorPlatform) => void;
  removing?: ConnectorPlatform | null;
}

/**
 * One tab per platform, so the review is a single table that scrolls as one.
 *
 * Stacking every platform on one page meant a scroll container per frame, which
 * stops working the moment a plan carries hundreds of line items. Tabs also let
 * the table own the page's vertical scroll, which is what keeps the column
 * header pinned.
 *
 * The error count is on the tab because tabs hide things: a blank required field
 * on a platform you are not looking at has to be visible from here, or the only
 * hint is a disabled Publish button.
 *
 * Each tab can be taken off the job. A platform with no account blocks Publish, so one
 * added by mistake left a finished plan unpublishable behind a tab with no way to
 * remove it. Never the last one, which would leave a job publishing nowhere.
 */
export function JobPlatformTabs({
  groups,
  value,
  onChange,
  showIssues,
  onRemove,
  removing = null,
}: JobPlatformTabsProps) {
  const { t } = useTranslation('projects');
  return (
    <nav
      className="shrink-0 rounded-lg bg-card px-3 pt-1.5 text-foreground"
      aria-label={t('projectDetail.jobPlatformTabs.platformsInThisJob')}
    >
      <div className="flex items-center gap-1 border-b border-border-light" role="tablist">
        {groups.map((group) => {
          const isActive = group.platform === value;
          const counts = countGroup(group);
          const unbound = group.connectorId === null;

          // Only a platform holding nothing published: the binding is how a live
          // object is found again, and the API refuses the rest anyway.
          const canRemove = onRemove && groups.length > 1;

          return (
            <div
              key={group.platform}
              className={`group -mb-px flex items-center border-b-2 transition-colors ${
                isActive ? 'border-primary' : 'border-transparent'
              }`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(group.platform)}
                data-testid={`job-platform-tab-${group.platform}`}
                className={`flex items-center gap-2 py-2 pl-3 text-sm font-medium transition-colors ${
                  canRemove ? 'pr-1' : 'pr-3'
                } ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <img
                  src={CONNECTOR_PLATFORM_ICONS[group.platform]}
                  alt=""
                  aria-hidden
                  className="size-4 shrink-0 rounded-sm object-contain"
                />
                {CONNECTOR_PLATFORM_LABELS[group.platform]}
                {/* Unbound outranks field errors, matching `planBlocker`: fixing
                  fields on a platform with nowhere to publish is wasted work. */}
                {/* An unbound account is a setup gap, not a QA finding, so it
                  shows before pre-flight has run. */}
                {unbound ? (
                  <Badge variant="warning" roundness="full" className="font-normal">
                    {t('projectDetail.jobPlatformTabs.noAccount')}
                  </Badge>
                ) : showIssues && counts.errors > 0 ? (
                  <Badge variant="destructive" roundness="full" className="font-normal">
                    <TriangleAlertIcon className="size-3" />
                    {counts.errors}
                  </Badge>
                ) : (
                  // Rows, not objects. Counting every level put "16" beside a sheet
                  // numbered 1 to 12, which reads as a miscount rather than as the two
                  // different true things they are.
                  <Badge
                    variant="ghost"
                    roundness="full"
                    className="border-border-light text-[10px]"
                    title={t('projectDetail.jobPlatformTabs.rowsInThisPlan', {
                      count: countRows(group),
                      noun: levelPluralLabel(
                        group.platform,
                        leafLevelOf(group.platform),
                      ).toLowerCase(),
                    })}
                  >
                    {countRows(group)}
                  </Badge>
                )}
              </button>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(group.platform)}
                  disabled={removing !== null}
                  aria-label={t('projectDetail.jobPlatformTabs.removeFromThisJob', {
                    platform: CONNECTOR_PLATFORM_LABELS[group.platform],
                  })}
                  data-testid={`job-platform-remove-${group.platform}`}
                  // Always visible rather than revealed on hover. The whole reason this
                  // exists is that there was no way to take a platform off a job and no
                  // way to discover one; a control you have to find by hovering is the
                  // same problem with an extra step.
                  className="mr-1 flex size-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-colors duration-fast ease-out hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <XIcon className="size-3.5" aria-hidden />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

JobPlatformTabs.displayName = 'JobPlatformTabs';
